import { isApiError } from "./errors";
import { apiPost, apiPut } from "./http";
import type { TenantDto } from "./types";

/** 开通或重置超级管理员密码时，响应中仅此一次包含明文口令。 */
export type TenantDtoWithGeneratedPassword = TenantDto & { generatedSuperAdminPassword?: string };

export interface CreatePlatformTenantBody {
  name: string;
  shortName?: string;
  city: string;
  province: string;
  district?: string;
  regionCode?: string;
  sport: string;
  adminName?: string;
  adminEmail: string;
  adminPhone?: string;
  plan: string;
  expiresAt?: string;
  code?: string;
  logoColor?: string;
  weChatPaySubMchId?: string;
  /** 上级租户 LegacyId；省略或空 = 无上级 */
  parentTenantId?: string | null;
  /** 登记证书编号（可选） */
  certNumber?: string;
  /** 团体登记证书图片 OSS URL（可选，可先上传图片再填 URL） */
  registrationCertImageUrl?: string;
}

export interface PlatformTenantPatchBody {
  status?: string;
  plan?: string;
  expiresAt?: string;
  name?: string;
  shortName?: string;
  code?: string;
  city?: string;
  province?: string;
  district?: string;
  regionCode?: string;
  sport?: string;
  adminEmail?: string;
  adminName?: string;
  adminPhone?: string;
  logoColor?: string;
  weChatPaySubMchId?: string;
  /** 上级租户 LegacyId；空字符串清除上级 */
  parentTenantId?: string | null;
  /** 为 true 时重置协会后台「超级管理员」登录密码，并在响应中返回 generatedSuperAdminPassword（仅一次）。 */
  regenerateSuperAdminPassword?: boolean;
  certNumber?: string;
  registrationCertImageUrl?: string;
  /** 是否开通 AI 创建活动（运营平台维护） */
  activityAiCreateEnabled?: boolean;
}

export function createPlatformTenant(body: CreatePlatformTenantBody) {
  return apiPost<TenantDtoWithGeneratedPassword>("/api/platform/tenants", body);
}

export function putPlatformTenant(legacyId: string, body: PlatformTenantPatchBody) {
  return apiPut<TenantDtoWithGeneratedPassword>(
    `/api/platform/tenants/${encodeURIComponent(legacyId)}`,
    body,
  );
}

/**
 * 重置租户协会后台超级管理员密码。
 * apiPut 已含 POST /update 回退；此处额外保留专用 POST 接口以兼容极旧后端。
 */
export async function resetPlatformTenantSuperAdminPassword(legacyId: string) {
  const path = `/api/platform/tenants/${encodeURIComponent(legacyId)}`;
  try {
    return await putPlatformTenant(legacyId, { regenerateSuperAdminPassword: true });
  } catch (e) {
    const postFallback = async () =>
      apiPost<TenantDtoWithGeneratedPassword>(`${path}/reset-super-admin-password`, {});
    if (!isApiError(e)) throw e;
    if (e.status === 404 || e.status === 405) return postFallback();
    if (e.status === 400 && /regenerateSuperAdminPassword|至少提供/i.test(e.message)) {
      return postFallback();
    }
    throw e;
  }
}
